"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { resolveTemplateKey } from "@/lib/invoice/document";
import { TEMPLATES } from "@/lib/invoice/templates";
import {
  COPY_LABELS,
  FORMATS,
  TAX_MODES,
  addDays,
  computeTotals,
  fmtMoney,
  lastDayOfMonth,
  monthLabel,
  num,
  parsePastedLines,
  projectLine,
  compareText,
  sortLines,
  suggestNumber,
  todayISO,
  uid,
} from "@/lib/invoice/utils";
import { Button, Card, Field, Input, Loading, Modal, Select, Textarea, cx, useFeedback } from "./ui";
import { currencyOptions, docTypeOptions, stateCode, taxModeOptions } from "./options";
import { CLIENT_BLANK, ClientForm } from "./ClientForm";
import InvoicePreview from "./InvoicePreview";
import TemplatePicker from "./TemplatePicker";
import { TagList, TagPicker, TagSelect } from "./Tags";
import { suggestTag, invoiceTags } from "@/lib/invoice/tags";
import { invoiceChecks, projectToLine } from "@/lib/invoice/billing";
import { saleAmount, saleToLine, unbilledSales } from "@/lib/invoice/sales";

// Pick the right GST treatment from where the client is.
export function autoTaxMode(client, settings, brand) {
  if (client?.taxMode) return client.taxMode;
  const country = (client?.country || "").trim().toLowerCase();
  const homeCountry = (brand?.country || "India").trim().toLowerCase();
  if (homeCountry !== "india") {
    // businesses outside India: charge VAT/sales tax at home, nothing abroad
    if (!country || country === homeCountry) return brand?.gstin ? "vat" : settings.defaults.taxMode === "vat" ? "vat" : "none";
    return "none";
  }
  if (country && country !== "india") return "export";
  if (!country && !client?.state) return settings.defaults.taxMode;
  const home = (brand?.state || "").toLowerCase();
  return client?.state && home && client.state.toLowerCase() !== home ? "inter" : "intra";
}

export function snapshotClient(c) {
  if (!c) return {};
  const { name, contactPerson, email, phone, address, area, city, state, postcode, country, gstin } = c;
  return { name, contactPerson, email, phone, address, area: area || "", city, state, postcode: postcode || "", country, gstin };
}

export function placeOfSupply(c) {
  if (!c?.state) return "";
  const code = stateCode(c.state);
  return code ? `${c.state} (${code})` : c.state;
}

export function clientDefaults(c, store, date) {
  const brand = store.brandById(c.brandId);
  const dueDays = c.dueDays !== "" && c.dueDays != null ? num(c.dueDays) : num(store.settings.defaults.dueDays);
  return {
    clientId: c.id,
    billTo: snapshotClient(c),
    brandId: brand?.id || "",
    currency: c.currency || store.settings.defaults.currency,
    taxMode: autoTaxMode(c, store.settings, brand),
    placeOfSupply: placeOfSupply(c),
    dueDate: dueDays ? addDays(date, dueDays) : "",
  };
}

const newLine = (defaults = {}) => ({ id: uid(), description: "", hsn: "", qty: 1, rate: "", taxRate: 18, ...defaults });

export default function InvoiceEditor() {
  const store = useInvoiceStore();
  const router = useRouter();
  const params = useSearchParams();
  const { toast, confirm } = useFeedback();
  const { settings } = store;
  const [inv, setInv] = useState(null);
  const [numberSeq, setNumberSeq] = useState(null); // null => manual number
  const [tab, setTab] = useState("edit");
  const [picker, setPicker] = useState(null);
  const [paste, setPaste] = useState(null);
  const [newClient, setNewClient] = useState(null);
  const [showDesign, setShowDesign] = useState(false);
  const [fxLoading, setFxLoading] = useState(false);
  const [dirty, setDirty] = useState(false);

  // ---------- initialise ----------
  useEffect(() => {
    if (!store.ready || inv) return;
    const id = params.get("id");
    const dup = params.get("duplicate");
    const d = settings.defaults;
    if (id) {
      const found = store.invoices.find((x) => x.id === id);
      if (found) {
        setInv(structuredClone(found));
        return;
      }
    }
    let base = {
      docType: params.get("type") || "invoice",
      status: "draft",
      brandId: settings.defaultBrandId,
      template: "",
      date: todayISO(),
      dueDate: d.dueDays ? addDays(todayISO(), d.dueDays) : "",
      currency: d.currency,
      taxMode: d.taxMode,
      clientId: "",
      billTo: {},
      items: [],
      discount: "",
      credit: "",
      creditLabel: "Credit Amount",
      notes: d.notes,
      terms: d.terms,
      copyLabel: COPY_LABELS[0],
      showHsn: !!d.showHsn,
      showClientLogo: d.showClientLogo,
      period: "",
      periodMonth: "",
      placeOfSupply: "",
      reference: "",
      fxRate: "",
      payments: [],
      history: [],
    };
    if (dup) {
      const src = store.invoices.find((x) => x.id === dup);
      if (src) {
        const { id: _i, number: _n, createdAt: _c, updatedAt: _u, history: _h, ...rest } = structuredClone(src);
        const asType = params.get("type") || rest.docType;
        base = {
          ...rest,
          docType: asType,
          status: "draft",
          payments: [],
          history: [],
          date: todayISO(),
          items: rest.items.map((l) => ({ ...l, id: uid() })),
        };
        if (asType === "credit") {
          base.againstNumber = src.number;
          base.againstId = src.id;
          base.credit = "";
          base.discount = "";
          base.dueDate = "";
          base.notes = `Credit note against invoice ${src.number}.`;
        } else if (src.dueDate && src.date) {
          const gap = Math.round((new Date(src.dueDate) - new Date(src.date)) / 864e5);
          base.dueDate = addDays(base.date, gap);
        }
      }
    }
    const client = store.clients.find((c) => c.id === params.get("client"));
    if (client) {
      base = { ...base, ...clientDefaults(client, store, base.date) };
      const ids = (params.get("projects") || "").split(",").filter(Boolean);
      const ym = base.periodMonth || (base.date || "").slice(0, 7);
      base.items = store.projects
        .filter((p) => ids.includes(p.id))
        .map((p) => (p.billing === "monthly" && projectToLine(p, ym, { basis: settings.defaults.prorate || "30" })) || projectToLine({ ...p, billing: "one-time" }, ym))
        .map((l) => newLine(l));
      const saleIds = (params.get("sales") || "").split(",").filter(Boolean);
      const sales = store.sales.filter((x) => saleIds.includes(x.id) && !x.invoiceId && x.clientId === client.id);
      base.items = [...base.items, ...sales.map((x) => saleToLine(x, store.projects, settings.defaults.taxRate))];
      base.items = sortLines(base.items);
    }
    const s = suggestNumber(settings, store.invoices, base.docType, base.date, store.brandById(base.brandId));
    base.number = s.number;
    setNumberSeq(s.seq);
    setInv(base);
  }, [store, settings, params, inv]);

  // Keep auto-number in sync with doc type / date / brand while untouched.
  useEffect(() => {
    if (!inv || inv.id || numberSeq == null) return;
    const s = suggestNumber(settings, store.invoices, inv.docType, inv.date, store.brandById(inv.brandId));
    if (s.number !== inv.number) {
      setInv((v) => ({ ...v, number: s.number }));
      setNumberSeq(s.seq);
    }
  }, [inv?.docType, inv?.date, inv?.brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn before leaving with unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const totals = useMemo(() => (inv ? computeTotals(inv) : null), [inv]);
  const set = (patch) => {
    setDirty(true);
    setInv((v) => ({ ...v, ...patch }));
  };
  const setLine = (id, patch) => set({ items: inv.items.map((l) => (l.id === id ? { ...l, ...patch } : l)) });

  const chooseClient = (id) => {
    const c = store.clients.find((x) => x.id === id);
    if (!c) return set({ clientId: "", billTo: {} });
    const defs = clientDefaults(c, store, inv.date);
    set(inv.id ? { clientId: defs.clientId, billTo: defs.billTo, placeOfSupply: defs.placeOfSupply } : defs);
  };

  const applyFormat = (key) => set({ currency: FORMATS[key].currency, taxMode: FORMATS[key].taxMode });

  const duplicateNumber = inv && store.invoices.some((x) => x.number === inv.number && x.id !== inv.id);
  const locked = inv?.id && inv.status !== "draft";

  const save = useCallback(
    async (status) => {
      if (!inv) return;
      if (!inv.clientId && !inv.billTo?.name) return toast("Choose a client first.", { tone: "error" });
      if (!inv.number?.trim()) return toast("Invoice number is required.", { tone: "error" });
      if (duplicateNumber && !(await confirm(`Number ${inv.number} is already used by another document. Save anyway?`, { okLabel: "Save anyway" }))) return;
      const items = inv.items
        .filter((l) => l.description?.trim() || num(l.rate))
        .map((l) => ({ ...l, qty: num(l.qty), rate: num(l.rate), taxRate: num(l.taxRate) }));
      if (!items.length) return toast("Add at least one line item.", { tone: "error" });
      items.splice(0, items.length, ...sortLines(items));
      const nextStatus = status || inv.status;
      const action = !inv.id ? (nextStatus === "draft" ? "Created as draft" : "Created and finalised") : inv.status === "draft" && nextStatus !== "draft" ? "Finalised" : "Edited";
      const record = store.saveInvoice(
        { ...inv, items, status: nextStatus, discount: num(inv.discount), credit: num(inv.credit), fxRate: inv.fxRate === "" ? "" : num(inv.fxRate) },
        { seqUsed: !inv.id && numberSeq != null ? numberSeq : null, action }
      );
      setDirty(false);
      toast("Saved", { tone: "success" });
      router.push(`/invoice/invoices/view?id=${record.id}`);
    },
    [inv, duplicateNumber, numberSeq, store, router, toast, confirm]
  );

  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [save]);

  if (!store.ready || !inv) return <Loading />;

  const taxable = inv.taxMode === "intra" || inv.taxMode === "inter" || inv.taxMode === "vat";
  // the client's projects/services plus reusable services offered to every client
  const clientProjects = store.projects.filter((p) => (inv.clientId && p.clientId === inv.clientId) || (p.kind === "service" && !p.clientId));
  // lines are always kept A–Z (blank lines stay at the bottom until typed in)
  const ordered = sortLines;
  const cur = inv.currency;
  const client = store.clients.find((c) => c.id === inv.clientId);
  const effectiveTemplate = resolveTemplateKey(inv, store);
  const usedSaleIds = new Set(inv.items.map((l) => l.saleId).filter(Boolean));
  const pendingSales =
    (inv.docType || "invoice") === "invoice" && inv.clientId ? unbilledSales(store.sales, { clientId: inv.clientId }).filter((x) => !usedSaleIds.has(x.id)) : [];

  const fetchFx = async () => {
    setFxLoading(true);
    try {
      const r = await fetch(`https://api.frankfurter.app/${inv.date}?from=${cur}&to=INR`);
      const j = await r.json();
      const rate = j?.rates?.INR;
      if (!rate) throw new Error("no rate");
      set({ fxRate: rate });
      toast(`1 ${cur} = ₹${rate} on ${j.date} (ECB reference rate)`);
    } catch {
      toast("Couldn’t fetch the rate – enter it manually (e.g. RBI/SBI TT rate).", { tone: "error" });
    } finally {
      setFxLoading(false);
    }
  };

  const openPicker = () => {
    const used = new Set(inv.items.map((l) => l.projectId).filter(Boolean));
    setPicker(new Set(clientProjects.filter((p) => p.clientId && p.status === "active" && !used.has(p.id)).map((p) => p.id)));
  };
  const addPicked = () => {
    const ym = inv.periodMonth || (inv.date || "").slice(0, 7);
    const lines = clientProjects
      .filter((p) => picker.has(p.id))
      .map((p) => (p.billing === "monthly" && projectToLine(p, ym, { basis: settings.defaults.prorate || "30" })) || projectToLine({ ...p, billing: "one-time" }, ym))
      .map((l) => newLine({ ...l, taxRate: l.taxRate || settings.defaults.taxRate }));
    set({ items: ordered([...inv.items.filter((l) => l.description || num(l.rate)), ...lines]) });
    setPicker(null);
  };

  const saveButtons = (
    <>
      <Button variant="secondary" onClick={() => save("draft")}>Save draft</Button>
      <Button onClick={() => save(inv.status === "draft" ? (inv.docType === "credit" ? "issued" : "sent") : inv.status)}>Save & finalise</Button>
    </>
  );

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[26px] font-semibold tracking-tight">{inv.id ? `Edit ${inv.number}` : `New ${docTypeOptions.find((o) => o.value === inv.docType)?.label.toLowerCase()}`}</div>
          <div className="text-sm text-slate-500">Ctrl/⌘ + S to save{dirty ? " · unsaved changes" : ""}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-lg border border-slate-300 bg-white p-0.5 text-sm">
            {["edit", "preview"].map((t) => (
              <button key={t} onClick={() => setTab(t)} className={cx("rounded-md px-3 py-1.5 capitalize", tab === t ? "bg-slate-900 text-white" : "text-slate-600")}>
                {t}
              </button>
            ))}
          </div>
          <Button variant="secondary" onClick={() => router.back()}>Cancel</Button>
          {saveButtons}
        </div>
      </div>

      {locked && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
          This document has already been finalised{inv.status === "sent" ? " and probably sent to the client" : ""}. For GST, prefer issuing a{" "}
          <b>credit note</b> instead of changing amounts on a sent invoice. Edits are recorded in the invoice history.
        </div>
      )}

      {tab === "preview" ? (
        <div className="overflow-x-auto rounded-2xl bg-slate-200/60 p-4 ring-1 ring-inset ring-slate-900/5 sm:p-6">
          <InvoicePreview invoice={inv} data={store} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[1fr_320px]">
          <div className="min-w-0 space-y-5">
            <Card
              title="Details"
              actions={Object.entries(FORMATS).map(([k, f]) => (
                <Button key={k} size="sm" variant={f.currency === cur && f.taxMode === inv.taxMode ? "primary" : "secondary"} onClick={() => applyFormat(k)}>
                  {f.label}
                </Button>
              ))}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Field label="Document type">
                  <Select value={inv.docType} onChange={(e) => set({ docType: e.target.value })} options={docTypeOptions} disabled={!!inv.id} />
                </Field>
                <Field label="Number" hint={duplicateNumber ? <span className="text-brand-600">Already used!</span> : numberSeq != null ? "Auto" : "Manual"}>
                  <div className="flex gap-1">
                    <Input
                      value={inv.number}
                      onChange={(e) => {
                        set({ number: e.target.value });
                        setNumberSeq(null);
                      }}
                    />
                    {!inv.id && (
                      <button
                        title="Regenerate next number"
                        className="rounded-lg border border-slate-300 px-2 text-slate-500 hover:bg-slate-50"
                        onClick={() => {
                          const s = suggestNumber(settings, store.invoices, inv.docType, inv.date, store.brandById(inv.brandId));
                          set({ number: s.number });
                          setNumberSeq(s.seq);
                        }}
                      >
                        ↻
                      </button>
                    )}
                  </div>
                </Field>
                <Field label="Date">
                  <Input type="date" value={inv.date} onChange={(e) => set({ date: e.target.value })} />
                </Field>
                {inv.docType !== "credit" ? (
                  <Field label={inv.docType === "quote" ? "Valid until" : "Due date"}>
                    <Input type="date" value={inv.dueDate} onChange={(e) => set({ dueDate: e.target.value })} />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {[["N/A", null], ["7d", 7], ["15d", 15], ["30d", 30], ["45d", 45]].map(([l, dd]) => (
                        <button key={l} className="rounded bg-slate-100 px-1.5 text-[11px] text-slate-600 hover:bg-slate-200" onClick={() => set({ dueDate: dd == null ? "" : addDays(inv.date, dd) })}>
                          {l}
                        </button>
                      ))}
                    </div>
                  </Field>
                ) : (
                  <Field label="Against invoice no.">
                    <Input value={inv.againstNumber || ""} onChange={(e) => set({ againstNumber: e.target.value })} />
                  </Field>
                )}
                <Field label="Currency">
                  <Select value={cur} onChange={(e) => set({ currency: e.target.value })} options={currencyOptions} />
                </Field>
                <Field label="Tax treatment" className={inv.taxMode === "vat" ? "" : "lg:col-span-2"}>
                  <Select value={inv.taxMode} onChange={(e) => set({ taxMode: e.target.value })} options={taxModeOptions} />
                </Field>
                {inv.taxMode === "vat" && (
                  <Field label="Tax name on invoice" hint="e.g. VAT, Sales tax, GST, HST">
                    <Input value={inv.taxLabel ?? ""} placeholder="VAT" onChange={(e) => set({ taxLabel: e.target.value })} />
                  </Field>
                )}
                {cur !== "INR" ? (
                  <Field label={`Rate: 1 ${cur} in INR`} hint="For GST returns (export value in ₹)">
                    <div className="flex gap-1">
                      <Input type="number" step="any" value={inv.fxRate ?? ""} onChange={(e) => set({ fxRate: e.target.value })} placeholder="e.g. 112.45" />
                      <button className="whitespace-nowrap rounded-lg border border-slate-300 px-2 text-xs text-slate-600 hover:bg-slate-50" onClick={fetchFx} disabled={fxLoading}>
                        {fxLoading ? "…" : "Fetch"}
                      </button>
                    </div>
                  </Field>
                ) : (
                  <div className="hidden lg:block" />
                )}
                <Field label="Billing period">
                  <Input type="month" value={inv.periodMonth || ""} onChange={(e) => set({ periodMonth: e.target.value, period: e.target.value ? monthLabel(e.target.value) : "" })} />
                  {inv.periodMonth && (
                    <button className="mt-1 text-[11px] text-brand-700 hover:underline" onClick={() => set({ date: lastDayOfMonth(inv.periodMonth) })}>
                      Set date to month end
                    </button>
                  )}
                </Field>
                <Field label="Place of supply">
                  <Input value={inv.placeOfSupply} onChange={(e) => set({ placeOfSupply: e.target.value })} placeholder="Rajasthan (08)" />
                </Field>
                <Field label="Reference / PO" className="lg:col-span-2">
                  <Input value={inv.reference} onChange={(e) => set({ reference: e.target.value })} />
                </Field>
                <Field
                  label="Extra tags"
                  className="sm:col-span-2 lg:col-span-4"
                  hint={
                    <span className="flex flex-wrap items-center gap-1">
                      Categories from the lines are included automatically
                      {invoiceTags({ items: inv.items }).length > 0 && (
                        <>
                          : <TagList ids={invoiceTags({ items: inv.items })} tags={settings.tags} small max={8} />
                        </>
                      )}
                    </span>
                  }
                >
                  <TagPicker tags={settings.tags} value={inv.tags || []} onChange={(tags) => set({ tags })} />
                </Field>
              </div>
            </Card>

            <Card
              title="Bill to"
              actions={<Button size="sm" variant="secondary" onClick={() => setNewClient({ ...CLIENT_BLANK })}>+ New client</Button>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Select value={inv.clientId} onChange={(e) => chooseClient(e.target.value)}>
                  <option value="">Choose client…</option>
                  {[...store.clients]
                    .filter((c) => !c.archived || c.id === inv.clientId)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </Select>
                {inv.billTo?.name && (
                  <div className="flex gap-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {client?.logo && <img src={client.logo} alt="" className="h-10 w-10 object-contain" />}
                    <div>
                      <div className="font-semibold">{inv.billTo.name}</div>
                      <div className="text-xs">
                        {[inv.billTo.address, inv.billTo.city, inv.billTo.country].filter(Boolean).join(", ")}
                        {inv.billTo.gstin && ` · GSTIN ${inv.billTo.gstin}`}
                      </div>
                      {inv.clientId && (
                        <button className="mt-1 text-[11px] text-brand-700 hover:underline" onClick={() => chooseClient(inv.clientId)}>
                          Refresh from client record
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card
              title={`Line items (${inv.items.length})`}
              subtitle="Always in A–Z order – lines sort themselves as you type"
              actions={
                <>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600">
                    <input type="checkbox" checked={!!inv.showHsn} onChange={(e) => set({ showHsn: e.target.checked })} /> HSN/SAC
                  </label>
                  <Button size="sm" variant="secondary" disabled={!inv.clientId || clientProjects.length === 0} onClick={openPicker}>
                    Add projects / services
                  </Button>
                  {pendingSales.length > 0 && (
                    <Button
                      size="sm"
                      onClick={() => {
                        set({ items: ordered([...inv.items.filter((l) => l.description || num(l.rate)), ...pendingSales.map((x) => saleToLine(x, store.projects, settings.defaults.taxRate))]) });
                        toast(`Added ${pendingSales.length} unbilled sale(s)`, { tone: "success" });
                      }}
                    >
                      + {pendingSales.length} unbilled sale{pendingSales.length > 1 ? "s" : ""} ({fmtMoney(pendingSales.reduce((t, x) => t + saleAmount(x), 0), cur)})
                    </Button>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => setPaste("")}>Paste lines</Button>
                  {inv.items.length > 0 && (
                    <Button size="sm" variant="ghost" onClick={async () => (await confirm("Remove all line items?", { okLabel: "Remove all", danger: true })) && set({ items: [] })}>
                      Clear
                    </Button>
                  )}
                </>
              }
            >
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead className="text-left text-[11px] uppercase text-slate-500">
                    <tr>
                      <th className="w-8 pb-2">#</th>
                      <th className="pb-2">Description</th>
                      {inv.showHsn && <th className="w-24 pb-2">HSN/SAC</th>}
                      <th className="w-16 pb-2">Qty</th>
                      <th className="w-28 pb-2">Rate</th>
                      {taxable && <th className="w-16 pb-2">GST %</th>}
                      <th className="w-32 pb-2">Category</th>
                      <th className="w-28 pb-2 text-right">Amount</th>
                      <th className="w-24 pb-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {inv.items.map((l, i) => (
                      <tr key={l.id} className="align-top">
                        <td className="py-1 pr-1 pt-3 text-xs text-slate-400">{i + 1}</td>
                        <td className="py-1 pr-1">
                          <Input
                            className="px-2 py-1.5"
                            value={l.description}
                            onChange={(e) => setLine(l.id, { description: e.target.value })}
                            onBlur={() =>
                              set({
                                items: sortLines(inv.items.map((x) => (x.id === l.id && !x.tag && x.description ? { ...x, tag: suggestTag(x.description, settings.tags) } : x))),
                              })
                            }
                            placeholder="Service description"
                          />
                        </td>
                        {inv.showHsn && (
                          <td className="py-1 pr-1">
                            <Input className="px-2 py-1.5" value={l.hsn} onChange={(e) => setLine(l.id, { hsn: e.target.value })} placeholder={settings.defaults.sac} />
                          </td>
                        )}
                        <td className="py-1 pr-1">
                          <Input className="px-2 py-1.5" type="number" step="any" value={l.qty} onChange={(e) => setLine(l.id, { qty: e.target.value })} />
                        </td>
                        <td className="py-1 pr-1">
                          <Input className="px-2 py-1.5" type="number" step="0.01" value={l.rate} onChange={(e) => setLine(l.id, { rate: e.target.value })} />
                        </td>
                        {taxable && (
                          <td className="py-1 pr-1">
                            <Input className="px-2 py-1.5" type="number" step="any" value={l.taxRate} onChange={(e) => setLine(l.id, { taxRate: e.target.value })} />
                          </td>
                        )}
                        <td className="py-1 pr-1">
                          <TagSelect className="w-full" tags={settings.tags} value={l.tag} onChange={(tag) => setLine(l.id, { tag })} placeholder="—" />
                        </td>
                        <td className="py-1 pr-1 pt-3 text-right font-medium">{fmtMoney(num(l.qty) * num(l.rate), cur)}</td>
                        <td className="whitespace-nowrap py-1 pt-2 text-right text-slate-400">
                          <button className="px-1 hover:text-slate-700" title="Duplicate line" onClick={() => set({ items: [...inv.items.slice(0, i + 1), { ...l, id: uid() }, ...inv.items.slice(i + 1)] })}>⧉</button>
                          <button className="px-1 hover:text-brand-600" title="Remove" onClick={() => set({ items: inv.items.filter((x) => x.id !== l.id) })}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="secondary" onClick={() => set({ items: [...inv.items, newLine({ taxRate: settings.defaults.taxRate })] })}>
                  + Add line
                </Button>
              </div>
            </Card>

            <Card
              title={`Design · ${store.brandById(inv.brandId)?.name || ""} · ${TEMPLATES[effectiveTemplate]?.label}`}
              actions={<Button size="sm" variant="secondary" onClick={() => setShowDesign((s) => !s)}>{showDesign ? "Hide" : "Change"}</Button>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Invoice as (business profile)">
                  <Select value={inv.brandId} onChange={(e) => set({ brandId: e.target.value })}>
                    {store.brands.map((b) => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </Select>
                </Field>
                <Field label="Copy label">
                  <Select value={inv.copyLabel} onChange={(e) => set({ copyLabel: e.target.value })}>
                    <option value="">(none)</option>
                    {COPY_LABELS.map((c) => <option key={c}>{c}</option>)}
                  </Select>
                </Field>
                <label className="mt-5 flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!inv.showClientLogo} onChange={(e) => set({ showClientLogo: e.target.checked })} disabled={!client?.logo} />
                  Show client logo {client?.logo ? "" : <span className="text-xs text-slate-400">(no logo on client)</span>}
                </label>
              </div>
              {showDesign && (
                <div className="mt-4">
                  <TemplatePicker
                    value={inv.template}
                    onChange={(template) => set({ template })}
                    invoice={inv}
                    data={store}
                    allowDefault
                    defaultLabel={`Default (${TEMPLATES[resolveTemplateKey({ ...inv, template: "" }, store)]?.label})`}
                  />
                </div>
              )}
            </Card>

            <Card title="Notes & terms">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Field label="Notes (shown on invoice)">
                  <Textarea value={inv.notes} onChange={(e) => set({ notes: e.target.value })} />
                </Field>
                <Field label="Terms & conditions">
                  <Textarea value={inv.terms} onChange={(e) => set({ terms: e.target.value })} />
                </Field>
              </div>
            </Card>
          </div>

          <div className="space-y-5">
            <ChecksCard inv={inv} store={store} onFix={(lineId, patch) => setLine(lineId, patch)} />
            <Card title="Summary" className="xl:sticky xl:top-6">
              <div className="space-y-2 text-sm">
                <Row label="Subtotal" value={fmtMoney(totals.subtotal, cur)} />
                {inv.taxMode === "intra" && (
                  <>
                    <Row label="CGST" value={fmtMoney(totals.cgst, cur)} />
                    <Row label="SGST" value={fmtMoney(totals.sgst, cur)} />
                  </>
                )}
                {inv.taxMode === "inter" && <Row label="IGST" value={fmtMoney(totals.igst, cur)} />}
                {inv.taxMode === "vat" && <Row label={inv.taxLabel || "VAT"} value={fmtMoney(totals.vat, cur)} />}
                {inv.taxMode === "export" && <Row label="IGST (export, 0%)" value={fmtMoney(0, cur)} />}
                {inv.docType !== "credit" && (
                  <>
                    <Field label="Discount">
                      <Input type="number" step="0.01" value={inv.discount} onChange={(e) => set({ discount: e.target.value })} />
                    </Field>
                    <Field label="Credit / adjustment">
                      <div className="flex gap-1">
                        <Input className="w-1/2" value={inv.creditLabel} onChange={(e) => set({ creditLabel: e.target.value })} />
                        <Input className="w-1/2" type="number" step="0.01" value={inv.credit} onChange={(e) => set({ credit: e.target.value })} />
                      </div>
                    </Field>
                    <Field label="Round off" hint="Small +/- adjustment so the total is a round figure">
                      <Input type="number" step="0.01" value={inv.roundOff ?? ""} onChange={(e) => set({ roundOff: e.target.value })} />
                    </Field>
                  </>
                )}
                <div className="border-t border-slate-200 pt-2">
                  <Row label={<b>Total</b>} value={<b className="text-lg">{fmtMoney(totals.total, cur)}</b>} />
                  {cur !== "INR" && num(inv.fxRate) > 0 && <Row label="≈ INR" value={fmtMoney(totals.total * num(inv.fxRate), "INR")} />}
                  {totals.paid > 0 && <Row label="Balance due" value={fmtMoney(totals.balance, cur)} />}
                </div>
                <div className="text-xs text-slate-500">{TAX_MODES[inv.taxMode]?.label}</div>
              </div>
              <div className="mt-4 grid gap-2">{saveButtons}</div>
            </Card>
          </div>
        </div>
      )}

      <Modal
        open={!!picker}
        onClose={() => setPicker(null)}
        title="Add projects & services"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setPicker(null)}>Cancel</Button>
            <Button onClick={addPicked}>Add {picker?.size || 0} lines</Button>
          </>
        }
      >
        {picker && (
          <div>
            <div className="mb-2 flex gap-3 text-xs">
              <button className="text-brand-700 hover:underline" onClick={() => setPicker(new Set(clientProjects.map((p) => p.id)))}>All</button>
              <button className="text-brand-700 hover:underline" onClick={() => setPicker(new Set(clientProjects.filter((p) => p.status === "active").map((p) => p.id)))}>Active only</button>
              <button className="text-brand-700 hover:underline" onClick={() => setPicker(new Set())}>None</button>
            </div>
            <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {[...clientProjects]
                .sort((a, b) => compareText(projectLine(a), projectLine(b)))
                .map((p) => (
                  <label key={p.id} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={picker.has(p.id)}
                      onChange={() =>
                        setPicker((s) => {
                          const n = new Set(s);
                          n.has(p.id) ? n.delete(p.id) : n.add(p.id);
                          return n;
                        })
                      }
                    />
                    <span className="flex-1">
                      {projectLine(p)}
                      {p.kind === "service" && <span className="ml-2 rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">{p.clientId ? "Service" : "Service · any client"}</span>}
                    </span>
                    <span className={cx("text-xs capitalize", p.status === "active" ? "text-emerald-600" : "text-slate-400")}>{p.status}</span>
                    <span className="w-24 text-right font-medium">{fmtMoney(num(p.rate), cur)}</span>
                  </label>
                ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={paste !== null}
        onClose={() => setPaste(null)}
        title="Paste lines from Excel / text"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setPaste(null)}>Cancel</Button>
            <Button
              onClick={() => {
                const rows = parsePastedLines(paste || "").map((r) => newLine({ ...r, taxRate: settings.defaults.taxRate, tag: suggestTag(r.description, settings.tags) }));
                set({ items: ordered([...inv.items.filter((l) => l.description || num(l.rate)), ...rows]) });
                setPaste(null);
              }}
            >
              Add {parsePastedLines(paste || "").length} lines
            </Button>
          </>
        }
      >
        <Field hint="One line per item: “Description ⇥ Rate”, “Description ⇥ Qty ⇥ Rate”, or “Description £66”.">
          <Textarea className="min-h-[240px] font-mono text-xs" value={paste || ""} onChange={(e) => setPaste(e.target.value)} />
        </Field>
      </Modal>

      <Modal
        open={!!newClient}
        onClose={() => setNewClient(null)}
        title="New client"
        wide
        footer={
          <>
            <Button variant="secondary" onClick={() => setNewClient(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!newClient.name.trim()) return toast("Client name is required", { tone: "error" });
                const c = store.saveClient(newClient);
                setNewClient(null);
                set(clientDefaults(c, store, inv.date));
              }}
            >
              Save & use
            </Button>
          </>
        }
      >
        {newClient && <ClientForm value={newClient} onChange={setNewClient} brands={store.brands} />}
      </Modal>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-600">{label}</span>
      <span>{value}</span>
    </div>
  );
}


// "Does this look right?" – compares project lines with their price and the last invoice.
export function ChecksCard({ inv, store, onFix }) {
  const checks = invoiceChecks(inv, store, { basis: store.settings.defaults.prorate || "30" });
  if (!checks.length) return null;
  return (
    <Card title={`Checks (${checks.length})`} subtitle="Compared with project prices and the last invoice" className="border-amber-200" data-testid="invoice-checks">
      <ul className="space-y-2 text-sm" data-testid="invoice-checks">
        {checks.map((c, i) => (
          <li key={i} className={cx("rounded-lg px-3 py-2", c.level === "warn" ? "bg-amber-50 text-amber-900" : "bg-sky-50 text-sky-900")}>
            {c.level === "warn" ? "⚠ " : "ℹ "}
            {c.text}
            {c.fix && onFix && (
              <button className="ml-2 text-xs font-semibold underline" onClick={() => onFix(c.lineId, c.fix)}>
                Use usual price
              </button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
